import { useCallback, useEffect, useMemo, useState } from 'react';
import { SAKTI_DEFAULTS } from '../apiConfig';
import SaktiApiService from '../services/saktiApiService';
import SaktiSpbApiService from '../services/saktiSpbApiService';
import { getTodayInJakartaForApi } from '../utils/date';
import {
  SaktiFilterOptions,
  SaktiPaginationMeta,
  SaktiSpbKpiData,
  SaktiSpbListItem,
  SaktiFilters,
} from '../types';

const EMPTY_OPTIONS: SaktiFilterOptions = {
  companies: [],
  estates: [],
  divisions: [],
};

const EMPTY_KPI: SaktiSpbKpiData = {
  total_spb: 0,
  total_bunch: 0,
  total_loose_fruit: 0,
  total_weight: 0,
};

const EMPTY_PAGINATION: SaktiPaginationMeta = {
  page: 1,
  limit: SAKTI_DEFAULTS.DEFAULT_LIMIT,
  total_items: 0,
  total_pages: 0,
};

interface UseSaktiSpbDashboardResult {
  options: SaktiFilterOptions;
  loadingOptions: boolean;
  loadingKpi: boolean;
  loadingTable: boolean;
  draftFilters: SaktiFilters;
  appliedFilters: SaktiFilters;
  kpi: SaktiSpbKpiData;
  rows: SaktiSpbListItem[];
  pagination: SaktiPaginationMeta;
  tableError: string | null;
  onDraftFilterChange: (nextFilters: SaktiFilters) => void;
  applyFilters: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  reloadAll: () => Promise<void>;
}

export const useSaktiSpbDashboard = (): UseSaktiSpbDashboardResult => {
  const initialFilters: SaktiFilters = useMemo(() => ({
    date: getTodayInJakartaForApi(),
    company_id: SAKTI_DEFAULTS.DEFAULT_COMPANY_ID,
  }), []);

  const [options, setOptions] = useState<SaktiFilterOptions>(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [loadingKpi, setLoadingKpi] = useState<boolean>(true);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [draftFilters, setDraftFilters] = useState<SaktiFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<SaktiFilters>(initialFilters);
  const [kpi, setKpi] = useState<SaktiSpbKpiData>(EMPTY_KPI);
  const [rows, setRows] = useState<SaktiSpbListItem[]>([]);
  const [pagination, setPagination] = useState<SaktiPaginationMeta>(EMPTY_PAGINATION);
  const [tableError, setTableError] = useState<string | null>(null);

  const fetchData = useCallback(async (filters: SaktiFilters, page: number = 1) => {
    setLoadingTable(true);
    setTableError(null);
    try {
      const params = { ...filters, page, limit: SAKTI_DEFAULTS.DEFAULT_LIMIT };
      const response = await SaktiSpbApiService.getList(params);
      if (response.success) {
        setRows(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      setTableError(error instanceof Error ? error.message : 'Gagal memuat data SPB');
    } finally {
      setLoadingTable(false);
    }
  }, []);

  const fetchKpi = useCallback(async (filters: SaktiFilters) => {
    setLoadingKpi(true);
    try {
      const response = await SaktiSpbApiService.getKpi(filters);
      if (response.success) {
        setKpi(response.data);
      }
    } catch (error) {
      // toast
    } finally {
      setLoadingKpi(false);
    }
  }, []);

  const loadFilterOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const fetchedOptions = await SaktiApiService.fetchFilterOptions();
      setOptions(fetchedOptions);
      
      const defaultCompany = fetchedOptions.companies.find(c => c.id === SAKTI_DEFAULTS.DEFAULT_COMPANY_ID)?.id 
        || fetchedOptions.companies[0]?.id 
        || SAKTI_DEFAULTS.DEFAULT_COMPANY_ID;

      const nextFilters = { ...initialFilters, company_id: defaultCompany };
      setDraftFilters(nextFilters);
      setAppliedFilters(nextFilters);
      
      fetchKpi(nextFilters);
      fetchData(nextFilters);
    } finally {
      setLoadingOptions(false);
    }
  }, [initialFilters, fetchKpi, fetchData]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const onDraftFilterChange = useCallback((nextFilters: SaktiFilters) => {
    setDraftFilters(nextFilters);
  }, []);

  const applyFilters = useCallback(async () => {
    setAppliedFilters(draftFilters);
    fetchKpi(draftFilters);
    fetchData(draftFilters, 1);
  }, [draftFilters, fetchKpi, fetchData]);

  const goToPage = useCallback(async (page: number) => {
    fetchData(appliedFilters, page);
  }, [appliedFilters, fetchData]);

  const reloadAll = useCallback(async () => {
    fetchKpi(appliedFilters);
    fetchData(appliedFilters, pagination.page);
  }, [appliedFilters, pagination.page, fetchKpi, fetchData]);

  return {
    options,
    loadingOptions,
    loadingKpi,
    loadingTable,
    draftFilters,
    appliedFilters,
    kpi,
    rows,
    pagination,
    tableError,
    onDraftFilterChange,
    applyFilters,
    goToPage,
    reloadAll,
  };
};

export default useSaktiSpbDashboard;
